import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiTruck, FiRefreshCw, FiShield, FiPhone } from 'react-icons/fi';
import { getFeatured } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import BrandLogo from '../components/common/BrandLogo';

const categories = [
  { label: 'Shirts',       href: '/shop/shirts',       emoji: '👔', desc: 'Casual to formal' },
  { label: 'Pants',        href: '/shop/pants',         emoji: '👖', desc: 'All fits & sizes' },
  { label: 'Baby Clothes', href: '/shop/baby-clothes',  emoji: '🍼', desc: 'Infant to 2 years' },
  { label: 'Kids',         href: '/shop/kids-clothes',  emoji: '🎒', desc: '3 to 12 years' },
  { label: 'Teens',        href: '/shop/teen-clothes',  emoji: '✨', desc: '13 to 17 years' },
];

const perks = [
  { icon: FiTruck,     title: 'Free Delivery',    desc: 'On orders above ৳1000' },
  { icon: FiRefreshCw, title: 'Easy Returns',     desc: '7-day hassle-free returns' },
  { icon: FiShield,    title: 'Secure Payments',  desc: 'bKash, Nagad, Card & COD' },
  { icon: FiPhone,     title: '24/7 Support',     desc: 'WhatsApp & email support' },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    getFeatured()
      .then(res => setFeatured(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* ── Hero ── */}
      <section className="relative bg-yamass-black text-white overflow-hidden min-h-[90vh] flex items-center">
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              #c8a96e 0px, #c8a96e 1px,
              transparent 1px, transparent 60px
            )`
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <p className="text-yamass-accent text-sm uppercase tracking-[0.3em] font-medium mb-4">
              New Collection — 2025
            </p>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-tight mb-6">
              Dress the
              <br />
              <span className="text-yamass-accent italic">Whole</span>
              <br />
              Family
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-md">
              Quality clothing for every age — from newborns to adults.
              Delivered across Bangladesh, right to your door.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 bg-yamass-accent text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-all"
              >
                Shop Now <FiArrowRight />
              </Link>
              <Link
                to="/shop/baby-clothes"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full text-sm font-semibold hover:border-yamass-accent hover:text-yamass-accent transition-all"
              >
                Baby Collection
              </Link>
            </div>
          </div>

          {/* Hero visual */}
          <div className="hidden md:flex items-center justify-center">
            <div className="relative w-[26rem] h-[26rem]">
              <div className="absolute inset-0 rounded-full bg-yamass-accent opacity-10 animate-pulse" />
              <div className="absolute inset-8 rounded-full bg-yamass-accent opacity-10" />
              <div className="absolute inset-0 flex items-center justify-center">
                <BrandLogo
                  size="xl"
                  to={null}
                  framed
                  className="bg-white/95 backdrop-blur-sm"
                  imageClassName="h-36"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Perks bar ── */}
      <section className="bg-yamass-cream border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {perks.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yamass-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-yamass-accent" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-yamass-black">{title}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Categories ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-yamass-accent text-sm uppercase tracking-widest mb-2">Browse by</p>
            <h2 className="font-display text-4xl font-bold text-yamass-black">Categories</h2>
          </div>
          <Link to="/shop" className="text-sm text-gray-500 hover:text-yamass-accent flex items-center gap-1 transition-colors">
            All products <FiArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {categories.map(cat => (
            <Link
              key={cat.href}
              to={cat.href}
              className="group bg-yamass-cream hover:bg-yamass-black rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{cat.emoji}</div>
              <h3 className="font-semibold text-yamass-black group-hover:text-white text-sm transition-colors">
                {cat.label}
              </h3>
              <p className="text-xs text-gray-400 group-hover:text-gray-400 mt-1">{cat.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Featured Products ── */}
      <section className="bg-yamass-cream py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-yamass-accent text-sm uppercase tracking-widest mb-2">Hand picked</p>
              <h2 className="font-display text-4xl font-bold text-yamass-black">Featured Products</h2>
            </div>
            <Link to="/shop" className="text-sm text-gray-500 hover:text-yamass-accent flex items-center gap-1 transition-colors">
              View all <FiArrowRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i}>
                  <div className="skeleton rounded-xl aspect-[3/4]" />
                  <div className="skeleton h-3 rounded mt-3 w-3/4" />
                  <div className="skeleton h-3 rounded mt-2 w-1/2" />
                </div>
              ))}
            </div>
          ) : featured.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {featured.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg mb-4">No featured products yet.</p>
              <Link to="/shop" className="text-yamass-accent hover:underline font-medium">
                Browse all products →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ── Banner CTA ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-yamass-green rounded-3xl p-12 md:p-16 text-white text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, #c8a96e 0%, transparent 50%), radial-gradient(circle at 80% 50%, #c8a96e 0%, transparent 50%)`
          }} />
          <div className="relative">
            <p className="text-yamass-accent/80 text-sm uppercase tracking-widest mb-3">Special offer</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
              Baby Collection
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
              Soft, safe, and affordable clothing for your little ones. From newborns to toddlers.
            </p>
            <Link
              to="/shop/baby-clothes"
              className="inline-flex items-center gap-2 bg-yamass-accent text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-all"
            >
              Shop Baby Clothes <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
